import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import {downloadData} from 'aws-amplify/storage';

export default function LinePlot({
    selectedState,
    selectedStateName,
    width = 640,
    height = 480,
    margin = { top: 50, right: 20, bottom: 40, left: 50 },
}) {
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);//to track loading state of the CSV
    const [data, setData] = useState([]);
    const [usData, setUSData] = useState([]);
    const [tooltipLeft, setTooltipLeft] = useState(false);
    const overlayRef = useRef();
    const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, year: null, score: null, usScore: null });
    const [csvText, setCSVText] = useState(null);

    useEffect(() => {
        const fetchCSV = async() => {
            try {
                const csvData = await downloadData({path: `solar_sent_2013_2022_sum.csv`}).result;
                const csvTextData = await csvData.body.text();
                setCSVText(csvTextData);
                const parsed = d3.csvParse(csvTextData);
                const usOnly = parsed.filter(d => d["State Abbr"] === "US")
                    .map(d => [+d.Year, +d["Solar Sentiment Score"]]);
                setUSData(usOnly);
            } catch(downloadErr) {
                setError(`S3 Download Error: ${downloadErr.message}`);
                setIsLoading(false);
            }
        }
        fetchCSV();
    }, []);
    useEffect(() => {
        if(usData.length > 0) {
            console.log(usData)
        }
    }, [usData]);
    useEffect(() => {
        setIsLoading(true);
        setError('');
        const fetchCSV = async() => {
            try {
                setData(d3.csvParse(csvText).filter(d => d["State Abbr"] === selectedState).map(d => [+d.Year, +d["Solar Sentiment Score"]]));
                setIsLoading(false);
            } catch(downloadErr) {
                setError(`S3 Download Error: ${downloadErr.message}`);
                setIsLoading(false);
            }
        }
        if(selectedState != null) {
            fetchCSV();
        }
    }, [selectedState])

    const handleMouseMove = (event) => {
        if(data.length === 0) return;
        const [mouseX, mouseY] = d3.pointer(event);
        const stateClosestPoint = data.reduce((prev, curr) => Math.abs(x(curr[0]) - mouseX) < Math.abs(x(prev[0]) - mouseX) ? curr : prev);
        const usClosestPoint = usData.reduce((prev, curr) => Math.abs(x(curr[0]) - mouseX) < Math.abs(x(prev[0]) - mouseX) ? curr : prev);

        if(stateClosestPoint[0] >= 2020) {
            setTooltipLeft(true);
        } else if(stateClosestPoint[0] < 2020) {
            setTooltipLeft(false);
        }

        d3.select(overlayRef.current).transition().duration(25).ease(d3.easeLinear).attr('x1', x(stateClosestPoint[0])).attr('x2', x(stateClosestPoint[0])).style("visibility", "visible");  
        setTooltip({ visible: true, x: mouseX + 5, y: mouseY + 25, year: `${stateClosestPoint[0]}`, score: `${stateClosestPoint[1]}`, usScore: `${usClosestPoint[1]}` });
    }

    const handleMouseLeave = () => {
        d3.select(overlayRef.current).style("visibility", "hidden");
        setTooltip({visible: false})
    };

    const gx = useRef();
    const gy = useRef();
    const x = d3.scaleLinear(
        [2013, 2013 + usData.length - 1], 
        [margin.left, width - margin.right]
    );
    let y = null;
    if(data.length > 0) {
        y = d3.scaleLinear().domain([Math.min(d3.min(usData, d => d[1]) - 0.5, d3.min(data, d => d[1]) - 0.5), Math.max(d3.max(usData, d => d[1]), d3.max(data, d => d[1]))]).range([height - margin.bottom, margin.top]).nice();
    } else {
        y = d3.scaleLinear().domain([Math.min(0, d3.min(usData, d => d[1])), d3.max(usData, d => d[1])]).range([height - margin.bottom, margin.top]).nice();
    }
    const line = d3.line().x((d, i) => x(d[0])).y(d => y(d[1]));
    useEffect(() => void d3.select(gx.current).call(d3.axisBottom(x).tickFormat(d3.format("d"))), [gx, x]);
    useEffect(() => void d3.select(gy.current).call(d3.axisLeft(y)), [gy, y]);
    return (
            <div style={{position: 'relative'}}>
                <svg width={width} height={height} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
                    <g ref={gx} transform={`translate(0,${height - margin.bottom})`} />
                    <g ref={gy} transform={`translate(${margin.left},0)`} />
                    <g stroke="lightgray" strokeWidth="1">
                        {y.ticks(10).map(tick => (
                            <line 
                                key={tick} 
                                x1={margin.left} 
                                x2={width - margin.right} 
                                y1={y(tick)} 
                                y2={y(tick)}
                            />
                        ))}
                    </g>
                    <text 
                        x={width / 2} 
                        y={height} 
                        textAnchor="middle"
                        fontSize="14px"
                    >
                        Year
                    </text>

                    <text 
                        x={-height / 2} 
                        y={15} 
                        textAnchor="middle" 
                        transform="rotate(-90)" 
                        fontSize="14px"
                    >
                        Solar Sentiment Score
                    </text>

                    <path fill="none" stroke="#E20C8F" strokeWidth="1.5" d={line(data)} />
                    <g fill="#E20C8F" stroke="#E20C8F" strokeWidth="1.5">
                        {data.map((d, i) => (<circle key={i} cx={x(d[0])} cy={y(d[1])} r="5.0" />))}
                    </g>
                    <path fill="none" stroke="#C2BEBF" strokeWidth="1.5" d={line(usData)} stroke-dasharray="2"/>
                    <g fill="#C2BEBF" stroke="#C2BEBF" strokeWidth="1.5">
                        {usData.map((d, i) => (<circle key={i} cx={x(d[0])} cy={y(d[1])} r="5.0" />))}
                    </g>
                    <line ref={overlayRef} stroke="black" strokeWidth=".5" style={{ visibility: "hidden" }} y1={margin.top} y2={height - margin.bottom} />
                    <g transform={`translate(${width - margin.left-110}, ${margin.top})`}>
                        <rect
                            x={-10}
                            y={-7}
                            width={160}
                            height={50}
                            rx={5}
                            ry={5}
                            fill="white"
                            stroke="rgba(0,0,0,0.1)"
                        />
                        <circle cx={10} cy={10} r={4} fill="#E20C8F" />
                        <line x1={0} y1={10} x2={20} y2={10} stroke="#E20C8F" strokeWidth="3"/>
                        <text fontSize={'14px'} x={25} y={15}>{selectedStateName}</text>

                        <circle cx={10} cy={25} r={4} fill="#C2BEBF" />
                        <line x1={0} y1={25} x2={20} y2={25} stroke="#C2BEBF" strokeWidth="3" stroke-dasharray="2"/>
                        <text fontSize={'14px'} x={25} y={30}>United States</text>
                    </g>
                </svg>
                {tooltip.visible && (
                    <div 
                        style={{ 
                            position: 'absolute', 
                            left: tooltipLeft == false ? tooltip.x + 10 : tooltip.x - 170,
                            top: tooltip.y - 10, 
                            background: 'white', 
                            padding: '5px', 
                            borderRadius: '3px',
                            fontSize: '12px',
                            boxShadow: '0 0 10px rgba(0, 0, 0, 0.4)',
                        }}
                    >
                        <h4>{tooltip.year}</h4>
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <svg width={12} height={8}>
                                <circle cx={4} cy={4} r={4} fill="#E20C8F" />
                            </svg>
                            <p>State Score <strong>{tooltip.score}</strong></p>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                            <svg width={12} height={8}>
                                <circle cx={4} cy={4} r={4} fill="#C2BEBF" />
                            </svg>
                            <p>United States Score <strong>{tooltip.usScore}</strong></p>
                        </div>
                    </div>
                )}
            </div>
    )
}